import axios from "axios";
import { CalendarIcon } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  FaBuilding,
  FaUser,
  FaBriefcase,
  FaMoneyBillWave,
  FaFileSignature,
  FaUpload,
  FaCalendarDays,
  FaXmark,
} from "react-icons/fa6";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../payslip/payslippicker.css";
const steps = [
  {
    id: 1,
    title: "Company\nInformation",
    mobileTitle: "Company\nInformation",
    icon: FaBuilding,
    fields: [
      "companyName",
      "companyEmail",
      "companyPhone",
      "companyWebsite",
      "companyAddress",
      "companyLogo",
      "signatoryName",
      "signatoryDesignation",
    ],
  },
  {
    id: 2,
    title: "Candidate\n Information",
    mobileTitle: "Candidate\n Information",
    icon: FaUser,
    fields: [
      "candidateName",
      "candidatePhone",
      "candidateEmail",
      "candidateAddress",
    ],
  },
  {
    id: 3,
    title: "Job\n Information",
    mobileTitle: "Job\n Information",
    icon: FaBriefcase,
    fields: [
      "jobTitle",
      "department",
      "employmentType",
      "workLocation",
      "workMode",
      "reportingManager",
      "joiningDate",
      "probationPeriod",
    ],
  },
  {
    id: 4,
    title: "Salary and\n  Benefits",
    mobileTitle: "Salary and \n Benefits",
    icon: FaMoneyBillWave,
    fields: [
      "annualCTC",
      "monthlySalary",
      "salaryCurrency",
      "bonus",
      "benefits",
    ],
  },
  {
    id: 5,
    title: "Offer \nDetails",
    mobileTitle: "Offer \nDetails",
    icon: FaFileSignature,
    fields: [
      "offerDate",
      "offerValidUntil",
      "noticePeriod",
      "workingDays",
      "workingHours",
      "additionalTerms",
    ],
  },
];

const defaultValues = {
  companyName: "",
  companyEmail: "",
  companyPhone: "",
  companyWebsite: "",
  companyAddress: "",
  companyLogo: null,
  signatoryName: "",
  signatoryDesignation: "",

  candidateName: "",
  candidatePhone: "",
  candidateEmail: "",
  candidateAddress: "",

  jobTitle: "",
  department: "",
  employmentType: "",
  workLocation: "",
  workMode: "",
  reportingManager: "",
  joiningDate: "",
  probationPeriod: "",

  annualCTC: "",
  monthlySalary: "",
  salaryCurrency: "INR",
  bonus: "",
  benefits: "",

  offerDate: "",
  offerValidUntil: "",
  noticePeriod: "",
  workingDays: "",
  workingHours: "",
  additionalTerms: "",
};

const inputClass =
  "w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm text-[#10324F] outline-none transition placeholder:text-slate-400 focus:border-0 focus:ring-0";

const labelClass = "mb-1.5 block text-sm font-medium text-[#10324F]";

const FormField = ({ label, required, error, children }) => {
  return (
    <div>
      <label className={labelClass}>
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>

      {children}

      {error && (
        <p className="mt-1 text-[10px] text-red-500">{error.message}</p>
      )}
    </div>
  );
};
const BASE_URL = import.meta.env.VITE_BASE_URL;

const OfferLetterGeneratorForm = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [logoPreview, setLogoPreview] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    trigger,
    setValue,
    watch,
    reset,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues,
    mode: "onBlur",
  });

  const values = watch();

  const currentStep = steps[activeStep];

  const handleNext = async () => {
    const valid = await trigger(currentStep.fields);

    // Company logo is manually handled
    if (activeStep === 0 && !logoPreview) {
      setError("companyLogo", {
        type: "required",
        message: "Company logo is required",
      });

      return;
    }

    if (!valid) return;

    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setValue("companyLogo", file, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });

    const url = URL.createObjectURL(file);
    setLogoPreview(url);
    clearErrors("companyLogo");

    // Revalidate the field
    //  await trigger("companyLogo");
  };
  const handleLogoChangedd = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    setValue("companyLogo", file, {
      shouldValidate: true,
      shouldDirty: true,
    });

    const url = URL.createObjectURL(file);
    setLogoPreview(url);
  };

  const onSubmit = async (data) => {
    try {
      if (isGenerating) return;

      setIsGenerating(true);
      const formData = new FormData();

      // Add normal fields
      Object.entries(data).forEach(([key, value]) => {
        if (key === "companyLogo") {
          if (value instanceof File) {
            formData.append("companyLogo", value);
          }
        } else {
          formData.append(key, value ?? "");
        }
      });

      const response = await axios.post(
        `${BASE_URL}/generate-offerletter`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          responseType: "blob",
        },
      );

      console.log("Response:", response);
      console.log("Content-Type:", response.headers["content-type"]);

      // Check that backend actually returned PDF
      const contentType = response.headers["content-type"];

      if (!contentType?.includes("application/pdf")) {
        const text = await response.data.text();

        console.error("Backend returned:", text);

        throw new Error("Backend did not return a PDF");
      }

      // PDF Blob
      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      // Create temporary URL
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Offer-Letter-${data.candidateName || "Candidate"}.pdf`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      // Open PDF in new tab
      window.open(url, "_blank");
      reset(defaultValues);

      // Clear logo preview
      setLogoPreview("");

      // Go back to first step
      setActiveStep(0);
      // Give browser time to open the blob
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 10000);
    } catch (error) {
      console.error("Offer letter generation failed:", error);

      // Axios blob errors need special handling
      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();

        try {
          const errorData = JSON.parse(text);

          console.error("Backend error:", errorData);

          alert(errorData.message || "Failed to generate offer letter");
        } catch {
          console.error("Server response:", text);
          alert("Failed to generate offer letter");
        }
      } else {
        alert(error.message || "Failed to generate offer letter");
      }
    } finally {
      setIsGenerating(false);
    }
  };
  const clearLogo = () => {
    setLogoPreview("");
    setValue("companyLogo", null, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };
  return (
    <section>
      <form className="w-full">
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(300px,0.9fr)]">
          {/* ================= FORM ================= bg-[#eef4f8]*/}
          <div className="min-w-0 rounded-xl border border-white  shadow-sm backdrop-blur-2xl p-4">
            {/* STEP NAVIGATION */}
            <div className="scrollbar-hide flex w-full gap-2 overflow-x-auto pb-2 sm:overflow-visible">
              {steps.map((step, index) => {
                const active = activeStep === index;

                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={async () => {
                      if (index <= activeStep) {
                        setActiveStep(index);
                        return;
                      }

                      const valid = await trigger(steps[activeStep].fields);

                      if (valid) {
                        setActiveStep(index);
                      }
                    }}
                    className={`flex min-w-26.25 shrink-0 flex-1 items-center justify-center rounded-md px-2 py-2.5 text-center transition                       
          sm:min-w-0 
          ${
            active
              ? " bg-[#10324F] text-white shadow-sm"
              : "border border-white  text-[#10324F] bg-linear-to-b from-white/60 to-[#D1DFEA]/30  shadow-sm hover:bg-white/70 "
          }`}
                  >
                    <span className="whitespace-pre-line text-xs leading-4 sm:text-sm">
                      {step.title}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* CURRENT STEP */}
            <div className="mt-5">
              {activeStep === 0 && (
                <CompanyInformation
                  register={register}
                  errors={errors}
                  handleLogoChange={handleLogoChange}
                  logoPreview={logoPreview}
                  clearLogo={clearLogo}
                />
              )}

              {activeStep === 1 && (
                <CandidateInformation register={register} errors={errors} />
              )}

              {activeStep === 2 && (
                <JobInformation
                  register={register}
                  errors={errors}
                  control={control}
                />
              )}

              {activeStep === 3 && (
                <SalaryInformation
                  register={register}
                  errors={errors}
                  watch={watch}
                  setValue={setValue}
                />
              )}

              {activeStep === 4 && (
                <OfferDetails
                  register={register}
                  errors={errors}
                  control={control}
                />
              )}
            </div>

            {/* NAVIGATION BUTTONS */}
            <div className="mt-7 flex items-center justify-center gap-3">
              {activeStep > 0 && (
                <button
                  type="button"
                  onClick={handlePrevious}
                  className="min-w-30 rounded-md border border-[#163957] px-6 py-2.5 text-sm font-medium text-[#163957] transition hover:bg-[#163957] hover:text-white"
                >
                  Previous
                </button>
              )}

              {activeStep < steps.length - 1 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="min-w-30 rounded-md bg-[#163957] px-8 py-2.5 text-sm font-medium text-white transition hover:bg-[#10324F]"
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  disabled={isGenerating}
                  onClick={handleSubmit(onSubmit)}
                  className={`min-w-45 rounded-md px-8 py-2.5 text-sm font-medium text-white transition ${
                    isGenerating
                      ? "cursor-not-allowed bg-slate-400"
                      : "bg-[#10324F] hover:bg-[#10324F]"
                  }`}
                >
                  {isGenerating ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Generating...
                    </span>
                  ) : (
                    "Generate Offer Letter"
                  )}
                </button>
              )}
            </div>
          </div>

          {/* ================= PREVIEW ================= */}
          <OfferLetterPreview values={values} logoPreview={logoPreview} />
        </div>
      </form>
    </section>
  );
};

export default OfferLetterGeneratorForm;

const CompanyInformation = ({
  register,
  errors,
  handleLogoChange,
  logoPreview,
  clearLogo,
}) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Company Information
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField label="Company Name" required error={errors.companyName}>
          <input
            {...register("companyName", {
              required: "Company name is required",
              minLength: {
                value: 2,
                message: "Enter a valid company name",
              },
            })}
            className={inputClass}
            placeholder="Enter Company name"
          />
        </FormField>

        <FormField label="Company Email" required error={errors.companyEmail}>
          <input
            type="email"
            {...register("companyEmail", {
              required: "Company email is required",
              pattern: {
                value:
                  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,3}(\.[A-Za-z]{2,3})?$/,
                message: "Enter a valid email",
              },
            })}
            className={inputClass}
            placeholder="Enter Company Email"
          />
        </FormField>

        <FormField label="Phone Number" required error={errors.companyPhone}>
          <input
            {...register("companyPhone", {
              required: "Phone number is required",
              pattern: {
                value: /^[0-9]{10}$/,
                message: "Enter 10 digit phone number",
              },
            })}
            type="tel"
            inputMode="numeric"
            maxLength={10}
            onKeyDown={(e) => {
              // Allow: numbers, Backspace, Delete, Tab, arrows
              if (
                !/[0-9]/.test(e.key) &&
                ![
                  "Backspace",
                  "Delete",
                  "Tab",
                  "ArrowLeft",
                  "ArrowRight",
                ].includes(e.key)
              ) {
                e.preventDefault();
              }
            }}
            onInput={(e) => {
              e.currentTarget.value = e.currentTarget.value
                .replace(/\D/g, "")
                .slice(0, 10);
            }}
            className={inputClass}
            placeholder="Enter Phone number"
          />
        </FormField>

        <FormField label="Website" error={errors.companyWebsite}>
          <input
            {...register("companyWebsite", {
              required: "Company website is required",
              pattern: {
                value: /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/,
                message: "Enter valid website",
              },
            })}
            className={inputClass}
            placeholder="Enter Company Website"
          />
        </FormField>

        <div className="sm:col-span-2">
          <FormField
            label="Company Address"
            required
            error={errors.companyAddress}
          >
            <input
              {...register("companyAddress", {
                required: "Company address is required",
              })}
              className={inputClass}
              placeholder="Enter Company Address"
            />
          </FormField>
        </div>
        <div className="sm:col-span-2">
          <FormField label="Company Logo" required error={errors.companyLogo}>
            {logoPreview ? (
              <div className="w-60">
                <div className="relative flex h-16 w-full items-center justify-center rounded-md border border-slate-200 bg-white">
                  <img
                    src={logoPreview}
                    alt="Company logo"
                    className="h-14 max-w-55 object-contain"
                  />

                  <button
                    type="button"
                    onClick={clearLogo}
                    className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white transition hover:bg-red-600"
                    aria-label="Remove logo"
                  >
                    <FaXmark className="text-xs" />
                  </button>
                </div>
              </div>
            ) : (
              <label className="flex h-16 cursor-pointer items-center justify-center rounded-md border border-dashed border-slate-300 bg-white transition hover:bg-slate-50">
                <div className="text-center text-[10px] text-[#10324F]">
                  <FaUpload className="mx-auto mb-1 text-[#10324F]/30" />
                  Upload Logo
                </div>

                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  className="hidden"
                  onChange={handleLogoChange}
                />
              </label>
            )}
          </FormField>
        </div>

        <FormField
          label="Authorised signatory name"
          required
          error={errors.signatoryName}
        >
          <input
            {...register("signatoryName", {
              required: "Signatory name is required",
            })}
            className={inputClass}
            placeholder="Enter signatory name"
          />
        </FormField>

        <FormField
          label="Signatory designation"
          required
          error={errors.signatoryDesignation}
        >
          <input
            {...register("signatoryDesignation", {
              required: "Designation is required",
            })}
            className={inputClass}
            placeholder="Enter designation"
          />
        </FormField>
      </div>
    </div>
  );
};

const CandidateInformation = ({ register, errors }) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Candidate Information
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <FormField label="Full Name" required error={errors.candidateName}>
            <input
              {...register("candidateName", {
                required: "Candidate name is required",
              })}
              className={inputClass}
              placeholder="Enter name"
            />
          </FormField>
        </div>

        <FormField label="Phone Number" required error={errors.candidatePhone}>
          <input
            {...register("candidatePhone", {
              required: "Phone number is required",
              pattern: {
                value: /^[0-9]{10}$/,
                message: "Enter 10 digit phone number",
              },
            })}
            type="tel"
            inputMode="numeric"
            maxLength={10}
            onKeyDown={(e) => {
              // Allow: numbers, Backspace, Delete, Tab, arrows
              if (
                !/[0-9]/.test(e.key) &&
                ![
                  "Backspace",
                  "Delete",
                  "Tab",
                  "ArrowLeft",
                  "ArrowRight",
                ].includes(e.key)
              ) {
                e.preventDefault();
              }
            }}
            onInput={(e) => {
              e.currentTarget.value = e.currentTarget.value
                .replace(/\D/g, "")
                .slice(0, 10);
            }}
            className={inputClass}
            placeholder="Enter Phone number"
          />
        </FormField>

        <FormField label="Email" required error={errors.candidateEmail}>
          <input
            type="email"
            {...register("candidateEmail", {
              required: "Email is required",
              pattern: {
                value:
                  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,3}(\.[A-Za-z]{2,3})?$/,
                message: "Enter valid email",
              },
            })}
            className={inputClass}
            placeholder="Enter Email"
          />
        </FormField>

        <div className="sm:col-span-2">
          <FormField label="Address" required error={errors.candidateAddress}>
            <input
              {...register("candidateAddress", {
                required: "Address is required",
              })}
              className={inputClass}
              placeholder="Enter Address"
            />
          </FormField>
        </div>
      </div>
    </div>
  );
};
const JobInformation = ({ register, errors, control }) => {
  const parseDateValue = (value) => {
    if (!value) return null;

    const [year, month, day] = String(value).split("-").map(Number);

    if (!year || !month || !day) return null;

    return new Date(year, month - 1, day);
  };

  const formatDateValue = (date) => {
    if (!date) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Job Information
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <FormField label="Job Title" required error={errors.jobTitle}>
            <input
              {...register("jobTitle", {
                required: "Job title is required",
              })}
              className={inputClass}
              placeholder="Enter Title"
            />
          </FormField>
        </div>

        <FormField label="Department" required error={errors.department}>
          <input
            {...register("department", {
              required: "Department is required",
            })}
            className={inputClass}
            placeholder="Enter Department"
          />
        </FormField>

        <FormField
          label="Employment Type"
          required
          error={errors.employmentType}
        >
          <select
            {...register("employmentType", {
              required: "Employment type is required",
            })}
            className={inputClass}
          >
            <option value="">Select Type</option>
            <option value="Full Time">Full Time</option>
            <option value="Part Time">Part Time</option>
            <option value="Contract">Contract</option>
            <option value="Internship">Internship</option>
          </select>
        </FormField>

        <div className="sm:col-span-2">
          <FormField label="Work Location" required error={errors.workLocation}>
            <input
              {...register("workLocation", {
                required: "Work location is required",
              })}
              className={inputClass}
              placeholder="Enter Location"
            />
          </FormField>
        </div>

        <FormField label="Work Mode" error={errors.workMode}>
          <select {...register("workMode")} className={inputClass}>
            <option value="">Select mode</option>
            <option value="Office">Office</option>
            <option value="Hybrid">Hybrid</option>
            <option value="Remote">Remote</option>
          </select>
        </FormField>

        <FormField label="Reporting Manager" error={errors.reportingManager}>
          <input
            {...register("reportingManager")}
            className={inputClass}
            placeholder="Enter manager name"
          />
        </FormField>

        {/* <FormField label="Date of joining" required error={errors.joiningDate}>
          <input
            type="date"
            {...register("joiningDate", {
              required: "Joining date is required",
            })}
            className={inputClass}
          />
        </FormField> */}

        <FormField label="Date of joining" required>
          <Controller
            name="joiningDate"
            control={control}
            rules={{
              required: "Joining date is required",
            }}
            render={({ field, fieldState }) => (
              <div className="relative joining-date-picker">
                <DatePicker
                  selected={parseDateValue(field.value)}
                  onChange={(date) => {
                    field.onChange(formatDateValue(date));
                  }}
                  onBlur={field.onBlur}
                  dateFormat="yyyy-MM-dd"
                  showPopperArrow={false}
                  isClearable
                  placeholderText="Select letter date"
                  showIcon
                  toggleCalendarOnIconClick
                  icon={<CalendarIcon className="h-4 w-4" />}
                  className={`${inputClass} h-10`}
                  wrapperClassName="w-full"
                  popperClassName="payslip-datepicker-popper react-popper-offset"
                  calendarClassName="payslip-datepicker"
                  popperPlacement="top-start"
                  portalId="datepicker-portal"
                />
                {fieldState.error && (
                  <p className="mt-1 text-[10px] text-red-500">
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />
        </FormField>

        <FormField label="Probation period" error={errors.probationPeriod}>
          <input
            {...register("probationPeriod")}
            className={inputClass}
            placeholder="Enter probation period"
          />
        </FormField>
      </div>
    </div>
  );
};
const SalaryInformation = ({ register, errors, watch, setValue }) => {
  const monthlySalary = watch("monthlySalary");

  useEffect(() => {
    const monthly = Number(monthlySalary);

    if (monthly > 0) {
      setValue("annualCTC", String(monthly * 12), {
        shouldValidate: true,
        shouldDirty: true,
      });
    } else {
      setValue("annualCTC", "", {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [monthlySalary, setValue]);

  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Salary and Benefits
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* Monthly Gross Salary */}
        <FormField
          label="Monthly gross salary"
          required
          error={errors.monthlySalary}
        >
          <input
            type="number"
            min="0"
            {...register("monthlySalary", {
              required: "Monthly salary is required",
              min: {
                value: 0,
                message: "Amount cannot be negative",
              },
              valueAsNumber: true,
            })}
            className={inputClass}
            placeholder="Enter Amount"
          />
        </FormField>
        {/* Annual CTC */}
        <FormField label="Annual CTC" error={errors.annualCTC}>
          <input
            type="number"
            readOnly
            {...register("annualCTC", {
              min: {
                value: 0,
                message: "Amount cannot be negative",
              },
            })}
            className={`w-full rounded-md border border-slate-200 px-3 py-2.5 text-sm text-[#10324F] outline-none transition placeholder:text-slate-400 focus:border-0 focus:ring-0 cursor-not-allowed bg-gray-300`}
            placeholder="Automatically calculated"
          />
        </FormField>

        {/* Currency */}
        <FormField
          label="Salary currency"
          required
          error={errors.salaryCurrency}
        >
          <select
            {...register("salaryCurrency", {
              required: "Currency is required",
            })}
            className={inputClass}
          >
            <option value="INR">INR</option>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
            <option value="GBP">GBP</option>
          </select>
        </FormField>

        {/* Bonus */}
        <FormField label="Bonus or incentive" error={errors.bonus}>
          <input
            type="number"
            min="0"
            {...register("bonus")}
            className={inputClass}
            placeholder="Enter Amount"
          />
        </FormField>

        {/* Benefits */}
        <div className="sm:col-span-2">
          <FormField label="Additional benefits" error={errors.benefits}>
            <input
              {...register("benefits")}
              className={inputClass}
              placeholder="Health insurance, travel allowance, ..."
            />
          </FormField>
        </div>
      </div>
    </div>
  );
};
const SalaryInformatiosssn = ({ register, errors }) => {
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Salary and Benefits
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField label="Annual CTC" required error={errors.annualCTC}>
          <input
            type="number"
            {...register("annualCTC", {
              required: "Annual CTC is required",
              min: {
                value: 0,
                message: "Amount cannot be negative",
              },
            })}
            className={inputClass}
            placeholder="Enter Amount"
          />
        </FormField>

        <FormField
          label="Monthly gross salary"
          required
          error={errors.monthlySalary}
        >
          <input
            type="number"
            {...register("monthlySalary", {
              required: "Monthly salary is required",
              min: {
                value: 0,
                message: "Amount cannot be negative",
              },
            })}
            className={inputClass}
            placeholder="Enter Amount"
          />
        </FormField>

        <FormField
          label="Salary currency"
          required
          error={errors.salaryCurrency}
        >
          <select
            {...register("salaryCurrency", {
              required: "Currency is required",
            })}
            className={inputClass}
          >
            <option value="INR">INR</option>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
            <option value="GBP">GBP</option>
          </select>
        </FormField>

        <FormField label="Bonus or incentive" error={errors.bonus}>
          <input
            {...register("bonus")}
            className={inputClass}
            placeholder="Enter Amount"
          />
        </FormField>

        <div className="sm:col-span-2">
          <FormField label="Additional benefits" error={errors.benefits}>
            <input
              {...register("benefits")}
              className={inputClass}
              placeholder="Health insurance, travel allowance, ..."
            />
          </FormField>
        </div>
      </div>
    </div>
  );
};
const OfferDetails = ({ register, errors, control }) => {
  const parseDateValue = (value) => {
    if (!value) return null;

    const [year, month, day] = String(value).split("-").map(Number);

    if (!year || !month || !day) return null;

    return new Date(year, month - 1, day);
  };

  const formatDateValue = (date) => {
    if (!date) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };
  return (
    <div>
      <h2 className="mb-5 text-sm font-semibold text-[#10324F] sm:text-base">
        Offer Details
      </h2>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField label="Offer letter date" required>
          <Controller
            name="offerDate"
            control={control}
            rules={{
              required: "Offer date is required",
            }}
            render={({ field, fieldState }) => (
              <div className="relative joining-date-picker">
                <DatePicker
                  selected={parseDateValue(field.value)}
                  onChange={(date) => {
                    field.onChange(formatDateValue(date));
                  }}
                  onBlur={field.onBlur}
                  dateFormat="yyyy-MM-dd"
                  showPopperArrow={false}
                  isClearable
                  placeholderText="Select letter date"
                  showIcon
                  toggleCalendarOnIconClick
                  icon={<CalendarIcon className="h-4 w-4" />}
                  className={`${inputClass} h-10`}
                  wrapperClassName="w-full"
                  popperClassName="payslip-datepicker-popper react-popper-offset"
                  calendarClassName="payslip-datepicker"
                  popperPlacement="top-start"
                  portalId="datepicker-portal"
                />
                {fieldState.error && (
                  <p className="mt-1 text-[10px] text-red-500">
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />
        </FormField>

        <FormField label="Offer valid until" required>
          <Controller
            name="offerValidUntil"
            control={control}
            rules={{
              required: "Offer validity date is required",
            }}
            render={({ field, fieldState }) => (
              <div className="relative joining-date-picker">
                <DatePicker
                  selected={parseDateValue(field.value)}
                  onChange={(date) => {
                    field.onChange(formatDateValue(date));
                  }}
                  onBlur={field.onBlur}
                  dateFormat="yyyy-MM-dd"
                  showPopperArrow={false}
                  isClearable
                  placeholderText="Select letter date"
                  showIcon
                  toggleCalendarOnIconClick
                  icon={<CalendarIcon className="h-4 w-4" />}
                  className={`${inputClass} h-10`}
                  wrapperClassName="w-full"
                  popperClassName="payslip-datepicker-popper react-popper-offset"
                  calendarClassName="payslip-datepicker"
                  popperPlacement="top-start"
                  portalId="datepicker-portal"
                />
                {fieldState.error && (
                  <p className="mt-1 text-[10px] text-red-500">
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />
        </FormField>
        <FormField label="Notice period" error={errors.noticePeriod}>
          <select {...register("noticePeriod")} className={inputClass}>
            <option value="">Select</option>
            <option value="15 Days">15 Days</option>
            <option value="30 Days">30 Days</option>
            <option value="60 Days">60 Days</option>
            <option value="90 Days">90 Days</option>
          </select>
        </FormField>
        <FormField label="Working days" error={errors.workingDays}>
          <select {...register("workingDays")} className={inputClass}>
            <option value="">Select days</option>
            <option value="Monday - Friday">Monday - Friday</option>
            <option value="Monday - Saturday">Monday - Saturday</option>
          </select>
        </FormField>
        <div className="sm:col-span-2">
          <FormField label="Working hours" error={errors.workingHours}>
            <input
              {...register("workingHours")}
              className={inputClass}
              placeholder="Enter time"
            />
          </FormField>
        </div>
        <div className="sm:col-span-2">
          <FormField
            label="Additional terms or joining instructions"
            error={errors.additionalTerms}
          >
            <input
              {...register("additionalTerms")}
              className={inputClass}
              placeholder="Enter Instructions"
            />
          </FormField>
        </div>
      </div>
    </div>
  );
};

const OfferLetterPreview = ({ values, logoPreview }) => {
  const formatDate = (date) => {
    if (!date) return "20 October, 2026";

    const d = new Date(date);

    if (Number.isNaN(d.getTime())) return date;

    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <div className="rounded-xl border border-white shadow-sm backdrop-blur-2xl p-4">
      <h2 className="mb-5 text-sm font-semibold text-[#10324F]">Preview</h2>

      {/* Preview paper */}
      <div className="mx-auto w-full bg-white">
        {/* Top blue shape */}
        {/* <div className="relative h-7 bg-[#d9e4ec]">
          <div className="absolute left-0 top-0 h-3 w-[72%] bg-[#163957] [clip-path:polygon(0_0,100%_0,92%_100%,0_100%)]" />
        </div> */}
        <Ribbon position="top" />

        {/* Document */}
        <div className="px-4 py-3">
          {/* Header */}
          <div className="flex items-center justify-between">
            {logoPreview ? (
              <img
                src={logoPreview}
                alt="Company"
                className="h-8 max-w-25 object-contain"
              />
            ) : (
              <span className="text-lg text-[#10324F]">Waggex</span>
            )}

            <div className="text-right">
              <p className="text-xs font-bold text-[#10324F]">OFFER LETTER</p>

              <p className="mt-1 text-xs text-[#10324F]">
                {formatDate(values.offerDate)}
              </p>
            </div>
          </div>

          {/* Recipient */}
          <div className="mt-7 text-xs leading-4 text-[#10324F]">
            <p className="font-semibold">To:</p>

            <p>{values.candidateName || "Mary Smith"}</p>

            <p>
              {values.candidateAddress ||
                "Phase 8-B, Industrial Area, Sector 74"}
            </p>
          </div>

          {/* Greeting */}
          <div className="mt-5">
            <p className="text-xs font-semibold text-[#10324F]">
              Dear {values.candidateName || "Mary Smith"},
            </p>

            <p className="mt-2 text-xs leading-3 text-[#10324F]">
              We are delighted to offer you the position of{" "}
              <strong>{values.jobTitle || "Graphic Designer"}</strong> at{" "}
              {values.companyName || "Waggex"}.
            </p>

            <p className="mt-2 text-xs leading-3 text-[#10324F]">
              Your creativity, skills, and passion for design will be a valuable
              addition to our team.
            </p>
          </div>

          {/* Offer details */}
          <div className="mt-5">
            <p className="text-xs font-semibold text-[#10324F]">
              Details of the Offer:
            </p>

            <ul className="mt-1 space-y-1 text-xs text-[#10324F]]">
              <li>• Position: {values.jobTitle || "Graphic Designer"}</li>

              <li>• Department: {values.department || "Design"}</li>

              <li>• Work Location: {values.workLocation || "Chandigarh"}</li>

              <li>• Joining Date: {formatDate(values.joiningDate)}</li>

              <li>
                • Salary:{" "}
                {values.monthlySalary
                  ? `${values.salaryCurrency || "INR"} ${values.monthlySalary}`
                  : "As per company standards"}
              </li>

              {values.probationPeriod && (
                <li>• Probation: {values.probationPeriod}</li>
              )}
            </ul>
          </div>

          {/* Terms */}
          <div className="mt-5 text-xs leading-3 text-[#10324F]">
            <p>
              We are confident that your contribution will help us grow and
              achieve new milestones. Please confirm your acceptance by replying
              to this letter before {formatDate(values.offerValidUntil)}.
            </p>
          </div>

          {/* Signature */}
          <div className="mt-6 text-xs text-[#10324F]">
            <p>Sincerely,</p>

            <p className="mt-3 font-semibold">
              {values.signatoryName || "Korina Villanueva"}
            </p>

            <p>{values.signatoryDesignation || "HR Manager"}</p>
          </div>

          {/* Footer */}
          <div className="mt-8 text-center text-xs text-[#10324F]">
            Powered by Waggex
          </div>
        </div>

        {/* Bottom blue shape */}
        <Ribbon position="bottom" />
        {/* <div className="relative h-5 bg-[#d9e4ec]">
          <div className="absolute bottom-0 right-0 h-3 w-[72%] bg-[#163957] [clip-path:polygon(8%_0,100%_0,100%_100%,0_100%)]" />
        </div> */}
      </div>
    </div>
  );
};

const Ribbon = ({ position = "top" }) => {
  const isTop = position === "top";

  return (
    <div className="relative -mx-px h-9.25 w-[calc(100%+2px)] overflow-hidden">
      {/* Green base */}
      <div className="absolute -inset-0.5 bg-[#10324F]" />

      <div
        className="absolute -inset-0.5"
        style={{
          transform: isTop ? "none" : "scaleX(-1) scaleY(-1)",
        }}
      >
        {/* Magenta ribbon */}
        <div
          className="absolute inset-0 bg-[#D1DFEA]"
          style={{
            clipPath:
              "polygon(76% 0%, 100% 0%, 100% 100%, 51% 100%, 47% 60%, 70% 55%)",
          }}
        />

        {/* White cut / separation */}
        <div
          className="absolute bottom-0 left-0 h-4 w-[55%] bg-white"
          style={{
            clipPath: "polygon(0 0, 88% 0, 100% 100%, 0 100%)",
          }}
        />
      </div>
    </div>
  );
};
